import { useState, useEffect, useCallback } from 'react';
import { Caption } from '../types';

export type EditMode = 'captions' | 'titles';

interface UseCaptionEditorProps {
  initialCaptions?: Caption[];
  initialTitles?: Caption[];
  initialMode?: EditMode;
}

interface UseCaptionEditorReturn {
  // Mode state
  editMode: EditMode;
  setEditMode: (mode: EditMode) => void;
  
  // Caption/Title data
  captions: Caption[];
  titles: Caption[];
  currentItems: Caption[];
  
  // Setters
  setCaptions: React.Dispatch<React.SetStateAction<Caption[]>>;
  setTitles: React.Dispatch<React.SetStateAction<Caption[]>>;
  setCurrentItems: React.Dispatch<React.SetStateAction<Caption[]>>;
  
  // Style application
  applyToAllCaptions: boolean;
  setApplyToAllCaptions: (value: boolean) => void;
  
  // Exclusion flags
  excludeCaptions: boolean;
  setExcludeCaptions: (value: boolean) => void;
  excludeTitles: boolean;
  setExcludeTitles: (value: boolean) => void;
  
  // Word count control
  maxWordsPerCaption: number;
  setMaxWordsPerCaption: (value: number) => void;
  
  // Helper functions
  getAllItems: () => Caption[];
  getFilteredItemsForExport: () => Caption[];
}

export const useCaptionEditor = ({
  initialCaptions = [],
  initialTitles = [],
  initialMode = 'captions'
}: UseCaptionEditorProps = {}): UseCaptionEditorReturn => {
  // Mode state
  const [editMode, setEditMode] = useState<EditMode>(initialMode);
  
  // Separate state for captions and titles
  const [captions, setCaptions] = useState<Caption[]>(initialCaptions);
  const [titles, setTitles] = useState<Caption[]>(initialTitles);
  
  // Style application flag
  const [applyToAllCaptions, setApplyToAllCaptions] = useState(true);
  
  // Exclusion flags for video generation
  const [excludeCaptions, setExcludeCaptions] = useState(false);
  const [excludeTitles, setExcludeTitles] = useState(false);
  
  // Word count control
  const [maxWordsPerCaption, setMaxWordsPerCaption] = useState(12);
  
  // Get current items based on edit mode
  const currentItems = editMode === 'captions' ? captions : titles;
  const setCurrentItems = editMode === 'captions' ? setCaptions : setTitles;
  
  // Set different defaults for Apply Style to All based on edit mode
  useEffect(() => {
    if (editMode === 'captions') {
      setApplyToAllCaptions(true); // Checked by default for captions
    } else {
      setApplyToAllCaptions(false); // Unchecked by default for titles
    }
  }, [editMode]);
  
  // Get all items (captions + titles)
  const getAllItems = useCallback((): Caption[] => {
    return [...captions, ...titles];
  }, [captions, titles]);
  
  // Get filtered items for export based on exclusion flags
  const getFilteredItemsForExport = useCallback((): Caption[] => {
    let items: Caption[] = [];
    if (!excludeCaptions) {
      items = [...items, ...captions];
    }
    if (!excludeTitles) {
      items = [...items, ...titles];
    }
    return items;
  }, [captions, titles, excludeCaptions, excludeTitles]);
  
  return {
    editMode,
    setEditMode,
    captions,
    titles,
    currentItems,
    setCaptions,
    setTitles,
    setCurrentItems,
    applyToAllCaptions,
    setApplyToAllCaptions,
    excludeCaptions,
    setExcludeCaptions,
    excludeTitles,
    setExcludeTitles,
    maxWordsPerCaption,
    setMaxWordsPerCaption,
    getAllItems,
    getFilteredItemsForExport
  };
};
